import { useCallback, useEffect, useRef, useState } from 'react';

import {
  assignAdminSubscription,
  deleteAdminPlan,
  deleteAdminProduct,
  getAdminPlans,
  getAdminSubscriptions,
  resetAdminMonthlyQuota,
  revokeAdminSubscription,
  type AdminPlanRow,
  type PaginationMeta,
  type AdminSubscriptionRow,
  updateAdminPlan,
} from '../api/admin';

interface UseAdminCatalogOptions {
  loadPlans?: boolean;
  loadSubscriptions?: boolean;
  enableAutoRefresh?: boolean;
}

export function useAdminCatalog(
  productCode?: string,
  query?: string,
  page = 1,
  pageSize = 10,
  options?: UseAdminCatalogOptions,
) {
  const {
    loadPlans = true,
    loadSubscriptions = true,
    enableAutoRefresh = true,
  } = options || {};
  const [plans, setPlans] = useState<AdminPlanRow[]>([]);
  const [subscriptions, setSubscriptions] = useState<AdminSubscriptionRow[]>([]);
  const [plansPagination, setPlansPagination] = useState<PaginationMeta>({ total: 0, page: 1, pageSize, totalPages: 1 });
  const [subscriptionsPagination, setSubscriptionsPagination] = useState<PaginationMeta>({ total: 0, page: 1, pageSize, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [mutating, setMutating] = useState(false);

  // Prevents duplicate concurrent refreshes (focus listener + interval + mutation
  // post-refresh could all fire simultaneously when the first request is slow).
  const refreshingRef = useRef(false);

  const refresh = useCallback(async () => {
    if (refreshingRef.current) return;
    refreshingRef.current = true;
    setLoading(true);
    try {
      if (loadPlans) {
        const plansResponse = await getAdminPlans({ productCode, q: query, page, pageSize });
        const plansPayload = plansResponse.data.data as any;
        const planItems = Array.isArray(plansPayload) ? plansPayload : (plansPayload?.items || []);
        setPlans(planItems);
        setPlansPagination(plansPayload?.pagination || {
          total: planItems.length,
          page,
          pageSize,
          totalPages: Math.max(1, Math.ceil(planItems.length / pageSize)),
        });
      } else {
        setPlans([]);
        setPlansPagination({ total: 0, page: 1, pageSize, totalPages: 1 });
      }

      if (loadSubscriptions) {
        const subscriptionsResponse = await getAdminSubscriptions({ productCode, q: query, page, pageSize });
        const subscriptionsPayload = subscriptionsResponse.data.data as any;
        const subscriptionItems = Array.isArray(subscriptionsPayload) ? subscriptionsPayload : (subscriptionsPayload?.items || []);
        setSubscriptions(subscriptionItems);
        setSubscriptionsPagination(subscriptionsPayload?.pagination || {
          total: subscriptionItems.length,
          page,
          pageSize,
          totalPages: Math.max(1, Math.ceil(subscriptionItems.length / pageSize)),
        });
      } else {
        setSubscriptions([]);
        setSubscriptionsPagination({ total: 0, page: 1, pageSize, totalPages: 1 });
      }
    } finally {
      setLoading(false);
      refreshingRef.current = false;
    }
  }, [loadPlans, loadSubscriptions, page, pageSize, productCode, query]);

  useEffect(() => {
    refresh().catch(() => {
      setPlans([]);
      setPlansPagination({ total: 0, page: 1, pageSize, totalPages: 1 });
      setSubscriptions([]);
      setSubscriptionsPagination({ total: 0, page: 1, pageSize, totalPages: 1 });
    });
  }, [refresh]);

  useEffect(() => {
    if (!enableAutoRefresh) return undefined;
    const interval = window.setInterval(() => {
      refresh().catch(() => undefined);
    }, 30_000);
    return () => window.clearInterval(interval);
  }, [enableAutoRefresh, refresh]);

  const wrapMutation = useCallback(async (work: () => Promise<void>) => {
    setMutating(true);
    try {
      await work();
      // Refresh in background — mutation resolves immediately so the
      // button/spinner unlocks without waiting for the slow list reload.
      refresh().catch(() => undefined);
    } finally {
      setMutating(false);
    }
  }, [refresh]);

  return {
    plans,
    plansPagination,
    subscriptions,
    subscriptionsPagination,
    loading,
    mutating,
    refresh,
    savePlan: (planId: number, payload: Parameters<typeof updateAdminPlan>[1]) =>
      wrapMutation(async () => { await updateAdminPlan(planId, payload); }),
    removePlan: (planId: number) =>
      wrapMutation(async () => { await deleteAdminPlan(planId); }),
    removeProduct: (productId: number) =>
      wrapMutation(async () => { await deleteAdminProduct(productId); }),
    assignSubscription: (payload: Parameters<typeof assignAdminSubscription>[0]) =>
      wrapMutation(async () => { await assignAdminSubscription(payload); }),
    revokeSubscription: (subscriptionId: number, cancelAtCycleEnd?: boolean) =>
      wrapMutation(async () => { await revokeAdminSubscription(subscriptionId, cancelAtCycleEnd); }),
    resetMonthlyQuota: (userId: number, productCode: string, type: 'analyses' | 'reanalyses' | 'both' = 'analyses') =>
      wrapMutation(async () => { await resetAdminMonthlyQuota(userId, productCode, type); }),
  };
}
